// MCP server registry browser (roadmap item 405 Phase C).
//
// Mounted at `/repos/:id/mcp` for repositories of format `mcp`. Lists the
// published servers grouped by name (artifact metadata via the generic
// `useRepoArtifacts` hook) with transport, declared tools and the project's
// approval status from the admin policy endpoint. Approval status is
// informational only: the enforcement lives in the adapter, which filters
// what agents can fetch.

import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Server } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import {
  useMcpPolicy,
  useRepoArtifacts,
  useRepoDetail,
  type McpApproval,
  type RepoArtifact,
} from '../../api/client';

interface Tool {
  name: string;
  description?: string;
}

interface Row {
  key: string;
  name: string;
  version: string;
  transport: string;
  tools: Tool[];
}

function toRows(artifacts: RepoArtifact[] | undefined): Row[] {
  if (!artifacts) return [];
  const rows: Row[] = [];
  for (const a of artifacts) {
    const m = a.metadata;
    if (m?.kind !== 'mcp.server') continue;
    const tools = Array.isArray(m.tools)
      ? (m.tools as unknown[]).filter(
          (x): x is Tool => !!x && typeof (x as Tool).name === 'string',
        )
      : [];
    rows.push({
      key: a.id,
      name: String(m.name ?? a.path),
      version: String(m.version ?? a.version ?? ''),
      transport: String(m.transport ?? ''),
      tools,
    });
  }
  rows.sort((x, y) => (x.name === y.name ? (x.version < y.version ? 1 : -1) : x.name < y.name ? -1 : 1));
  return rows;
}

// Mirrors the adapter's precedence: a version row beats the server-wide row
// (version ''), and under enforcement no row means not approved.
export function approvalFor(
  approvals: McpApproval[],
  name: string,
  version: string,
): McpApproval | undefined {
  return (
    approvals.find((a) => a.name === name && a.version === version) ??
    approvals.find((a) => a.name === name && a.version === '')
  );
}

export function McpServers() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: repo, isLoading, error } = useRepoDetail(id);
  const { data: artifacts } = useRepoArtifacts(repo?.id, 1000);
  const { data: policy, error: policyError } = useMcpPolicy(repo?.projectId);
  const rows = useMemo(() => toRows(artifacts), [artifacts]);

  if (isLoading) return <div className="p-6 text-fg-muted">{t('mcpServers.loadingRepo')}</div>;
  if (error || !repo) {
    return (
      <div className="p-6 text-danger">
        {t('mcpServers.loadRepoFailed', { error: String(error ?? 'unknown') })}
      </div>
    );
  }
  if (repo.format !== 'mcp') {
    return (
      <div className="p-6">
        <Link to={`/repos/${repo.id}`} className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg">
          <ArrowLeft className="h-4 w-4" />
          {t('mcpServers.backToRepo')}
        </Link>
        <p className="text-fg-muted mt-2">{t('mcpServers.wrongFormat')}</p>
      </div>
    );
  }

  const status = (r: Row) => {
    if (policyError || !policy) return t('mcpServers.status.unknown');
    if (!policy.enforce) return t('mcpServers.status.open');
    const a = approvalFor(policy.servers, r.name, r.version);
    return a?.decision === 'approved' ? t('mcpServers.status.approved') : t('mcpServers.status.blocked');
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/repos/${repo.id}`} className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2">
          <ArrowLeft className="h-4 w-4" />
          {t('mcpServers.backTo', { name: repo.name })}
        </Link>
        <h1 className="text-2xl font-display font-semibold text-fg">{repo.name}</h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">{t('mcpServers.intro')}</p>
      </div>
      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Server className="h-4 w-4 text-accent" />
              {t('mcpServers.title')}
            </span>
          }
          description={
            policy
              ? t(policy.enforce ? 'mcpServers.enforced' : 'mcpServers.notEnforced')
              : undefined
          }
        />
        <CardBody>
          {rows.length === 0 ? (
            <p className="text-fg-muted text-sm">{t('mcpServers.empty')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-muted">
                  <th className="py-2">{t('mcpServers.col.server')}</th>
                  <th>{t('mcpServers.col.version')}</th>
                  <th>{t('mcpServers.col.transport')}</th>
                  <th>{t('mcpServers.col.tools')}</th>
                  <th>{t('mcpServers.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-t border-border">
                    <td className="py-2 font-mono">{r.name}</td>
                    <td className="font-mono">{r.version}</td>
                    <td>{r.transport}</td>
                    <td>{r.tools.map((x) => x.name).join(', ') || '—'}</td>
                    <td>
                      <Badge>{status(r)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
